package no.vareflow.app.data.local

/**
 * Where a platform keeps the app's local data as text values: SharedPreferences on the phone,
 * localStorage in the browser. Values are the JSON produced by [LocalCodec].
 */
interface KeyValueStore {
    fun getString(key: String): String?

    /** Writes several values together; a null value removes the key. */
    fun putStrings(values: Map<String, String?>)
}

/** Key names used since v0.2. Never rename: existing installs read these. */
object StorageKeys {
    const val USERS = "users"
    const val PRODUCTS = "products"
    const val SESSIONS = "sessions"
    const val INVENTORY_SESSIONS = "inventorySessions"
    const val CATEGORY_ASSIGNMENTS = "categoryAssignments"
    const val DEPARTMENTS = "departments"
    const val SELECTED_DEPARTMENTS = "selectedDepartments"
    const val COUNT_ENTRIES = "countEntries"
    const val CATEGORIES = "categories"
    const val CATEGORY_ORDER = "categoryOrder"
    const val PRODUCT_ORDERS = "productOrders"
    const val SPOT_CHECKS = "spotChecks"
    const val AUDIT_EVENTS = "auditEvents"
    const val CURRENT_INVENTORY_SESSION_ID = "currentInventorySessionId"
    const val FORCE_OFFLINE = "forceOffline"
    const val DELETED_PRODUCT_IDS = "deleted_product_ids"
    const val DELETED_CATEGORY_NAMES = "deleted_category_names"
    const val COUNT_OUTBOX = "count_outbox"
    const val SVINN = "svinn"
}

/**
 * Parses one stored collection with [parse]. Each key is read independently, so one damaged
 * value does not stop the rest from loading. A value that cannot be parsed is first copied to
 * a `corrupt_backup_<key>_<time>` key, so the next save cannot destroy it, and [onCorrupt] is
 * told the backup key. Returns false when the stored value was unreadable; a missing key counts
 * as success. Same rules as the Android LocalStore.
 */
fun KeyValueStore.readCollection(
    key: String,
    now: Long,
    parse: (String) -> Unit,
    onCorrupt: (backupKey: String) -> Unit
): Boolean {
    val raw = getString(key) ?: return true
    return try {
        parse(raw)
        true
    } catch (error: Exception) {
        val backupKey = "corrupt_backup_${key}_$now"
        putStrings(mapOf(backupKey to raw))
        onCorrupt(backupKey)
        false
    }
}
